/**
 * OosReviewQueuePage — Story 3.7: Admin OOS review queue (AC #1).
 *
 * Displays pending OOS-flagged chat messages for staff:rd and staff:admin review.
 * Staff can mark items as reviewed and add notes.
 *
 * Columns: anonymisedQuestion, detectionReason, occurredAt, status
 * Action: "Mark reviewed" button → dialog with notes field → PATCH API call
 *
 * URL routing (FIX 3 — supervised pass):
 *   Uses VITE_SERVICES_BASE_URL (shared API gateway) + /v1/chat/admin/... gateway path.
 *   Gateway route: ANY /v1/chat/{proxy+} → rewrite /v1/$request.path.proxy
 *   Client /v1/chat/admin/oos-review-queue → proxy=admin/oos-review-queue → /v1/admin/oos-review-queue ✓
 *   (pr #272 wires VITE_SERVICES_BASE_URL into admin-portal CI build; 3.7 merges after #272)
 *
 * @PHI: anonymisedQuestion is PHI-derived. Display only — never log or export.
 * @Auth: staff:rd role required (RequireAuth in app.tsx routing).
 */

import { usePortalAuth } from '@nrx/auth-ui';
import { Table } from '@nrx/ui';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';

const SERVICES_BASE_URL: string =
  (import.meta.env['VITE_SERVICES_BASE_URL'] as string | undefined) ?? '';

// ─── Types ────────────────────────────────────────────────────────────────────

interface OosReviewItem {
  id: string;
  memberId: string;
  anonymisedQuestion: string;
  detectionReason: string;
  occurredAt: string;
  status: 'pending' | 'reviewed';
  reviewedAt?: string;
  reviewNotes?: string;
  reviewerId?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function OosReviewQueuePage(): JSX.Element {
  const auth = usePortalAuth();
  const accessToken = auth.user?.access_token;

  const [items, setItems] = useState<OosReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Review dialog state
  const [reviewingItem, setReviewingItem] = useState<OosReviewItem | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    void fetchItems();
  }, [accessToken]);

  async function fetchItems(): Promise<void> {
    if (!accessToken) {
      setFetchError('Not authenticated — please sign in again.');
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(
        `${SERVICES_BASE_URL}/v1/chat/admin/oos-review-queue?status=pending`,
        {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      if (!res.ok) {
        setFetchError(`Failed to load review queue (${res.status})`);
        setLoading(false);
        return;
      }
      const data = (await res.json()) as { items: OosReviewItem[] };
      setItems(data.items ?? []);
    } catch {
      setFetchError('Unable to connect to API.');
    } finally {
      setLoading(false);
    }
  }

  function openReviewDialog(item: OosReviewItem): void {
    setReviewingItem(item);
    setReviewNotes('');
    setSubmitError(null);
  }

  function closeDialog(): void {
    setReviewingItem(null);
    setReviewNotes('');
    setSubmitError(null);
  }

  async function handleMarkReviewed(): Promise<void> {
    if (!reviewingItem || !accessToken) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      const res = await fetch(
        `${SERVICES_BASE_URL}/v1/chat/admin/oos-review-queue/${reviewingItem.id}/review`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ notes: reviewNotes }),
        },
      );
      if (!res.ok) {
        setSubmitError(`Review failed (${res.status})`);
        return;
      }
      closeDialog();
      // Refresh queue
      setLoading(true);
      setFetchError(null);
      await fetchItems();
    } catch {
      setSubmitError('Unable to connect to API.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="p-8 max-w-5xl">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">OOS Review Queue</h1>
        <p className="mt-1 text-sm text-gray-500">
          Pending out-of-scope chat messages flagged for Clinical Team review. Review weekly before
          model or corpus updates.
        </p>
      </div>

      {/* PHI notice */}
      <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
        <p className="text-xs text-amber-700">
          <strong>PHI notice:</strong> Flagged messages contain anonymised member questions
          (PHI-derived). Do not copy or share outside the clinical review workflow.
        </p>
      </div>

      {fetchError !== null && <p className="mt-6 text-red-600">{fetchError}</p>}

      {fetchError === null && (
        <div className="mt-6">
          {!loading && items.length === 0 ? (
            <p className="text-gray-500 text-sm">No pending items. Queue is clear.</p>
          ) : (
            <Table.ScrollArea loading={loading} loadingLabel="Loading…">
              <Table.Root>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell>Flagged Question</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Detection Reason</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Occurred At</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Action</Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {items.map((item) => (
                    <Table.Row key={item.id} className="hover:bg-gray-50">
                      <Table.Cell className="max-w-xs truncate text-gray-800">
                        {item.anonymisedQuestion}
                      </Table.Cell>
                      <Table.Cell className="text-gray-600">
                        <span className="rounded bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
                          {item.detectionReason}
                        </span>
                      </Table.Cell>
                      <Table.Cell className="text-gray-500">
                        {new Date(item.occurredAt).toLocaleString()}
                      </Table.Cell>
                      <Table.Cell>
                        <button
                          type="button"
                          onClick={() => openReviewDialog(item)}
                          className="rounded bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-700"
                        >
                          Mark reviewed
                        </button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Table.ScrollArea>
          )}
        </div>
      )}

      {/* Review dialog */}
      {reviewingItem !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Review OOS item"
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
        >
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Mark as Reviewed</h2>
            <div className="mb-4 p-3 bg-gray-50 rounded text-sm text-gray-700">
              <p>
                <strong>Question:</strong> {reviewingItem.anonymisedQuestion}
              </p>
              <p className="mt-1">
                <strong>Detection:</strong> {reviewingItem.detectionReason}
              </p>
            </div>
            <div>
              <label
                htmlFor="review-notes"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Notes
              </label>
              <textarea
                id="review-notes"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-md text-sm px-3 py-2"
                placeholder="Optional review notes…"
              />
            </div>
            {submitError !== null && <p className="mt-2 text-sm text-red-600">{submitError}</p>}
            <div className="mt-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeDialog}
                disabled={submitting}
                className="px-4 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleMarkReviewed()}
                disabled={submitting}
                className="px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
